import type {
  ChangePasswordSchema,
  ForgotPasswordSchema,
  ResetPasswordSchema,
  SignInSchema,
  UpdateProfileSchema,
} from '@/api/auth/auth.schema';
import { useRouter } from '@/hooks/use-router';
import { useToast } from '@/hooks/use-toast';
import { http } from '@/lib/http';
import type { ApiError } from '@/types/api/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Role, type User } from 'generated/prisma/browser';
import { useRouteError } from 'react-router-dom';

interface AuthResponse {
  success: boolean;
  user: User;
  accessToken: string;
}

interface UpdateProfileResponse {
  success: boolean;
  passwordChanged: boolean;
  user: User;
}

export const authApi = {
  signIn: async (data: SignInSchema) => {
    const res = await http.post<AuthResponse>('auth/sign-in', data);
    return res.data;
  },
  forgotPassword: async (data: ForgotPasswordSchema) => {
    await http.post('auth/forgot-password', data);
  },
  resetPassword: async (data: ResetPasswordSchema) => {
    await http.post('auth/reset-password', data);
  },
  signOut: async () => {
    await http.post('auth/sign-out');
  },

  getCurrentUser: async () => {
    const res = await http.get<User | null>('auth/me');
    return res.data;
  },

  changePassword: async (data: ChangePasswordSchema) => {
    await http.put('auth/me/password', data);
  },

  updateProfile: async (data: Partial<UpdateProfileSchema>) => {
    const res = await http.put<UpdateProfileResponse>('auth/me', data);
    return res.data;
  },
};

export const authKey = 'auth';

export const authKeys = {
  all: [authKey] as const,
  user: () => [...authKeys.all, 'user'] as const,
};

export const useSignIn = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const router = useRouter();

  return useMutation<AuthResponse, ApiError, SignInSchema>({
    mutationFn: authApi.signIn,
    onSuccess: async ({ user }) => {
      queryClient.setQueryData(authKeys.user(), user);
      toast.success('Login realizado com sucesso!');
      router.push('/admin');
    },
  });
};

export const useForgotPassword = () =>
  useMutation<void, ApiError, ForgotPasswordSchema>({ mutationFn: authApi.forgotPassword });

export const useResetPassword = () =>
  useMutation<void, ApiError, ResetPasswordSchema>({ mutationFn: authApi.resetPassword });

export const useSignOut = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: authApi.signOut,
    onSuccess: async () => {
      queryClient.setQueryData(authKeys.user(), null);
      queryClient.removeQueries();
      toast.success('Logout realizado com sucesso!');
    },
  });
};

export const useCurrentUser = () => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: authApi.getCurrentUser,
    staleTime: Infinity,
    retry: false,
    throwOnError: false,
  });
};

/** Atualiza nome, e-mail ou foto do usuário logado; o menu lateral reflete na hora. */
export const useUpdateMe = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateProfileResponse, ApiError, Partial<UpdateProfileSchema>>({
    mutationFn: authApi.updateProfile,
    onSuccess: ({ user }) => queryClient.setQueryData(authKeys.user(), user),
  });
};

export const useChangePassword = () =>
  useMutation<void, ApiError, ChangePasswordSchema>({ mutationFn: authApi.changePassword });

export const useIsAuthenticated = () => {
  const { data: user, isLoading } = useCurrentUser();
  return {
    isAuthenticated: !!user,
    isLoading,
    user,
  };
};

export const useUserPermissions = () => {
  const { data: user } = useCurrentUser();

  return {
    isAdmin: user?.role === Role.ADMIN,
  };
};
